import React, {useEffect, useMemo, useState} from 'react';
import {Button, Divider, Modal, NavBar, Toast} from 'antd-mobile';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {TaskPath} from "../../../enums/DeliveryNavigatePathEnum";
import { getDeliveringListPath } from "../../../utils/deliveryRouteUtils";
import {useDeliveryTaskOrderOperations} from '../store/deliveryStore';
import {useLocationStore} from "../../../store/locationStore";
import {t} from "../../../i18n";
import {GET_NEW_ASSIGNMENT_CONFIRM_LIST, NewAssignmentConfirmListResponse, OtherDriverDeliveryTaskOrderType} from "../graphql/queries/deliveryTaskOrderList";
import {CheckCircleFill} from 'antd-mobile-icons';
import customTopToast from "../../../components/CustomTopToast";
import {showErrorToast} from "../../../utils/exceptionUtils";
import {getUserInfo} from "../../../utils/auth";
import {useQuery} from "@apollo/client";
import {formatStopNumber} from "../../../utils/stopNumberUtils";

type OrderItem = {
    id: string;
    route: string;
    stopNumber: string;
};

const NewAssignmentsConfirmPage: React.FC = () => {
    const navigate = useNavigate();

    const { data: confirmData, loading: confirmLoading } = useQuery<NewAssignmentConfirmListResponse>(
        GET_NEW_ASSIGNMENT_CONFIRM_LIST,
        { fetchPolicy: 'network-only' }
    );

    const confirmPayload = confirmData?.getNewAssignmentConfirmList;

    const notScannedOrders = useMemo((): OrderItem[] => {
        const list = confirmPayload?.currentDriverUnScanList || [];
        return list.map(item => ({
            id: item.trackingNumber || '',
            route: item.routeName || '',
            stopNumber: formatStopNumber(item.stopNumber || 0, item.subStopNumber)
        }));
    }, [confirmPayload?.currentDriverUnScanList]);

    const otherDriverDeliveryTaskOrderList: OtherDriverDeliveryTaskOrderType[] =
        confirmPayload?.otherDriverDeliveryTaskOrderList || [];
    const otherDriverDeliveryTaskOrderDisplayList: string[] =
        confirmPayload?.otherDriverDeliveryTaskOrderDisplayList || [];
    const orderTotal = confirmPayload?.totalNeedScanCount ?? 0;
    const scannedTotal = confirmPayload?.scannedSuccessCount ?? 0;
    const hasUnScannedOrders = notScannedOrders.length > 0;
    const hasOtherDriverScannedOrders = otherDriverDeliveryTaskOrderList.length > 0;

    const {position} = useLocationStore();
    const {handleNewAssignmentOutForDelivery, handleSubmitWhsReview} = useDeliveryTaskOrderOperations();
    const [scanCompleted, setScanCompleted] = useState(false);
    const [loading,setLoading] = useState(false);

    useEffect(() => {
        if (orderTotal === scannedTotal && !hasUnScannedOrders && !hasOtherDriverScannedOrders) {
            setScanCompleted(true);
        } else {
            setScanCompleted(false);
        }
    }, [orderTotal, scannedTotal, hasUnScannedOrders, hasOtherDriverScannedOrders]);

    const handleStartAnyway = async () => {
        setLoading(true);
        try {
            const currentUser = getUserInfo();
            const result = await handleNewAssignmentOutForDelivery({
                orderId: "",
                location: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                }
            }, currentUser?.account?.name || null);
            if (result) {
                const deliveringPath = await getDeliveringListPath();
                navigate(deliveringPath);
                customTopToast.show({
                    content: (
                        <div className="flex items-center justify-center gap-2">
                            <CheckCircleFill className="text-2xl text-green-500"/>
                            <span className="text-xs">{t(`pages.delivery.task.startConfirm.startDeparture`)}</span>
                        </div>
                    ),
                    position: 'top',
                    duration: 3000
                });
            }
        } catch (error) {
            showErrorToast(error);
        }finally {
            setLoading(false);
        }
    };

    const handleSubmitReview = async () => {
        setLoading( true);
        try {
            const currentUser = getUserInfo();
            const result = await handleSubmitWhsReview(currentUser?.account?.name || null);
            if (result) {
                Modal.show({
                    header: (
                        <div className="flex items-center justify-center gap-2">
                            <CheckCircleFill className="text-4xl text-green-500"/>
                            <span
                                className="text-xl font-medium">{t(`pages.delivery.task.startConfirm.submissionSuccessful`)}</span>
                        </div>
                    ),
                    content: (
                        <div className="text-center">
                            <p><span
                                className="text-red-500">{t(`pages.delivery.task.startConfirm.submissionDesc1`)}</span>{t(`pages.delivery.task.startConfirm.submissionDesc2`)}
                            </p>
                        </div>
                    ),
                    closeOnAction: true,
                    actions: [
                        {
                            key: 'confirm',
                            text: t(`pages.gotIt`),
                            primary: true,
                        },
                    ],
                    onClose: () => {
                        navigate(TaskPath.DELIVERY_TASK);
                    },
                })
            }
        } catch (error) {
            console.error('handleSubmitReview error:', error);
            Toast.show({
                icon: 'error',
                content: "Submit for Review Failed!",
            });
        }finally {
            setLoading( false);
        }
    };

    const handleContinueScanning = () => {
        navigate(TaskPath.DELIVERY_TASK_ASSIGNMENTS);
    };

    return (
        <div className="flex flex-col h-screen bg-white">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleContinueScanning} backIcon={scanCompleted ? false : <ArrowLeftOutlined/>}>
                    {t('pages.delivery.task.startConfirm.title')}
                </NavBar>
            </div>

            <div className="flex-1 overflow-auto px-4 pt-4">
                {confirmLoading && (
                    <div className="mb-2 text-center text-sm text-gray-500">{t('pages.delivery.task.startConfirm.loadingFooter')}</div>
                )}
                {/* 统计信息 */}
                <div className="rounded-xl bg-[#f3eefc] py-3 text-center text-[18px] font-semibold">
                    {t('pages.delivery.task.startConfirm.ordersAssigned')}: {orderTotal}
                </div>
                <div className="mt-5 text-[20px] font-semibold">
                    {t('pages.delivery.task.startConfirm.scannedSuccessfully')}: {scannedTotal}
                </div>

                <Divider className="my-4" />

                {/* 未扫描的订单 */}
                <div>
                    <div
                        className="mb-2 text-[20px] font-semibold">{t('pages.delivery.task.startConfirm.ordersNotScanned')}: {notScannedOrders.length}</div>
                    {notScannedOrders.length > 0 && (
                        <div
                            className="mb-2 max-w-full text-left text-[14px] font-semibold leading-snug text-[#ff6a00] break-words whitespace-normal"
                        >
                            {t('pages.delivery.task.startConfirm.continueScanOrSubmitReview')}
                        </div>
                    )}
                    <div className="max-h-[24vh] overflow-y-auto pr-1">
                        {notScannedOrders.map((order) => (
                            <div key={order.id} className="mb-1 break-all pl-2 text-[18px] text-[#555]">
                                {order.id} - {order.route} - {order.stopNumber}
                            </div>
                        ))}
                    </div>
                </div>

                <Divider className="my-4" />

                {/* 扫描他人包裹 */}
                <div>
                    <div className="mb-2 text-[20px] font-semibold">
                        {t('pages.delivery.task.startConfirm.scannedOtherDriverPackages', { count: otherDriverDeliveryTaskOrderDisplayList.length })}
                    </div>
                    {otherDriverDeliveryTaskOrderDisplayList.length > 0 && (
                        <div
                            className="mb-2 max-w-full text-left text-[14px] font-semibold leading-snug text-[#ff6a00] break-words whitespace-normal"
                        >
                            {t('pages.delivery.task.startConfirm.returnToWhsAndSubmitReview')}
                        </div>
                    )}
                    <div className="max-h-[24vh] overflow-y-auto pr-1">
                        {otherDriverDeliveryTaskOrderDisplayList.map((displayText, index) => {
                            return (
                                <div key={`${displayText}-${index}`} className="mb-1 break-all pl-2 text-[18px] text-[#555]">
                                    {displayText}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <Divider className="my-4" />
            </div>

            {/* 底部按钮：查询未完成前不渲染真实分支，避免列表为空时误判为可开始派送 */}
            {confirmLoading ? (
                <div {...getBottomNavBarContainerStyle('mb-4 flex w-full justify-center px-4')}>
                    <Button color="primary" className="w-full" loading disabled>
                        {t('pages.delivery.task.startConfirm.loadingFooter')}
                    </Button>
                </div>
            ) : hasUnScannedOrders ? (
                <div {...getBottomNavBarContainerStyle('flex gap-3 mb-4 justify-center w-full px-4')}>
                    <Button onClick={handleSubmitReview} style={{backgroundColor: '#6b7280', color: 'white'}}
                            className="flex-1" loading={loading}>
                        {t(`pages.submitReview`)}
                    </Button>
                    <Button onClick={handleContinueScanning} color="primary" className="flex-1">
                        {t(`pages.continueScanning`)}
                    </Button>
                </div>
            ) : hasOtherDriverScannedOrders ? (
                <div {...getBottomNavBarContainerStyle('flex gap-3 mb-4 justify-center')}>
                    <Button onClick={handleSubmitReview} style={{backgroundColor: '#6b7280', color: 'white'}}
                            className="[width:95%]" loading={loading}>
                        {t(`pages.submitReview`)}
                    </Button>
                </div>
            ) : (
                <div {...getBottomNavBarContainerStyle('flex gap-3 mb-4 justify-center')}>
                    <Button onClick={handleStartAnyway} color='primary' className="[width:95%]" loading={loading}>
                        {t(`pages.startDelivery`)}
                    </Button>
                </div>
            )}

        </div>
    );
};

export default NewAssignmentsConfirmPage;
